import { useCallback } from 'react';
import { useAudio } from '../contexts/AudioContext';

/**
 * Hook for playing game sounds with haptic feedback
 * Phase 1: Essential sounds only (dice, mark, turn, timer)
 */
const useGameSounds = () => {
  const { playSound, stopSound, vibrate } = useAudio();

  // ============================================
  // ESSENTIAL SOUNDS (Phase 1)
  // ============================================

  /**
   * Play dice roll sound with vibration
   * Triggered when: Player clicks "Roll Dice" button
   * Sync: All players hear this
   */
  const playDiceRoll = useCallback(() => {
    playSound('diceRoll');
    vibrate([50, 30, 50]); // Dice vibration pattern
  }, [playSound, vibrate]);

  /**
   * Play mark number sound with subtle vibration
   * Triggered when: Player marks a number on their scorecard
   * Sync: Local only
   */
  const playMarkNumber = useCallback(() => {
    playSound('markNumber');
    vibrate(20);
  }, [playSound, vibrate]);

  /**
   * Play turn change sound
   * Triggered when: Turn passes to next player
   * Sync: All players hear this
   */
  const playTurnChange = useCallback(() => {
    playSound('turnChange');
  }, [playSound]);

  /**
   * Play timer tick sound with vibration
   * Triggered when: Timer reaches last 5 seconds
   * Sync: Local only
   */
  const playTimerTick = useCallback(() => {
    playSound('timerTick');
    vibrate(30);
  }, [playSound, vibrate]);

  /**
   * Stop timer tick sound
   * Triggered when: Timer reaches 0
   */
  const stopTimerTick = useCallback(() => {
    stopSound('timerTick');
  }, [stopSound]);

  // ============================================
  // FEEDBACK SOUNDS (Phase 2 - placeholder)
  // ============================================

  const playGoodMove = useCallback(() => {
    // Phase 2: playSound('goodMove');
    vibrate([30, 20, 30]);
  }, [vibrate]);

  const playPenalty = useCallback(() => {
    // Phase 2: playSound('penalty');
    vibrate(100);
  }, [vibrate]);

  const playRowLocked = useCallback(() => {
    // Phase 2: playSound('rowLocked');
    vibrate([50, 30, 50, 30, 100]);
  }, [vibrate]);

  // ============================================
  // EVENT SOUNDS (Phase 2 - placeholder)
  // ============================================

  const playWinner = useCallback(() => {
    // Phase 2: playSound('winner');
    vibrate([100, 50, 100, 50, 200]);
  }, [vibrate]);

  const playHighscore = useCallback(() => {
    // Phase 2: playSound('highscore');
  }, []);

  // ============================================
  // OTHER PLAYER SOUNDS (Phase 2 - placeholder)
  // ============================================

  const playOtherPlayerMark = useCallback(() => {
    // Phase 2: playSound('otherPlayerMark');
  }, []);

  return {
    // Phase 1 - Essential
    playDiceRoll,
    playMarkNumber,
    playTurnChange,
    playTimerTick,
    stopTimerTick,

    // Phase 2 - Feedback (placeholder with haptic only)
    playGoodMove,
    playPenalty,
    playRowLocked,

    // Phase 2 - Events (placeholder)
    playWinner,
    playHighscore,

    // Phase 2 - Other players (placeholder)
    playOtherPlayerMark,
  };
};

export default useGameSounds;
